import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PHOTOS } from "@/lib/assets";

const VALUES = [
  { title: "Confianza", desc: "Condiciones claras y comunicación directa." },
  { title: "Profesionalidad", desc: "Una gestión organizada y un alcance definido." },
  { title: "Autonomía", desc: "Tú decides tus límites y el contenido que autorizas." },
  {
    title: "Proyección",
    desc: "Estrategia para desarrollar tu presencia digital y explorar oportunidades comerciales.",
  },
];

export default function Nosotros() {
  return (
    <div data-testid="nosotros-page">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">Nosotros</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 leading-[1.1] max-w-3xl">
              Una visión, liderada por su fundadora.
            </h1>
            <div className="mt-8 space-y-5 text-base sm:text-lg font-light text-stone2 leading-relaxed max-w-2xl">
              <p>
                Exclusive Society nace de la visión de Marjolaine Rachel, con el propósito de construir una agencia
                donde la creatividad, la estrategia y el desarrollo femenino se conviertan en oportunidades reales.
              </p>
              <p>
                Creemos en el potencial de cada mujer para construir su propio camino, desarrollar su marca personal y
                alcanzar nuevas metas.
              </p>
              <p>
                Nuestra filosofía combina profesionalismo, innovación y una visión moderna del crecimiento, ofreciendo
                acompañamiento estratégico y herramientas para potenciar el talento de nuestras creadoras.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start" data-testid="founder-profile-marjolaine">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-full h-full border border-champagne" aria-hidden="true" />
                  <img
                    src={PHOTOS.founder}
                    alt="Marjolaine Rachel, Fundadora y Directora de Exclusive Society"
                    className="relative w-full h-auto"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <h3 className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">Marjolaine Rachel</h3>
                <p className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold mt-2 mb-8">
                  Fundadora &amp; Directora de Exclusive Society
                </p>
                <div className="space-y-5 text-base font-light text-stone2 leading-relaxed">
                  <p>
                    Soy Marjolaine Rachel, fundadora y directora de Exclusive Society. Mi experiencia combina creación
                    de contenido, comunicación digital y gestión comercial.
                  </p>
                  <p>
                    Conozco el trabajo que hay detrás de una imagen: pensar una idea, producir contenido, editarlo y
                    comunicarlo de una manera que conecte con una audiencia. Me interesa unir esa creatividad con una
                    estrategia organizada y objetivos claros.
                  </p>
                  <p>
                    Creo en el emprendimiento femenino y en la importancia de que cada mujer pueda explorar sus
                    posibilidades con información y acompañamiento. Para mí, potenciar tu imagen también significa
                    respetar tu personalidad, tus decisiones y tus límites.
                  </p>
                  <p>
                    En Exclusive Society aporto una mirada creativa, comercial y cercana para ayudarte a desarrollar un
                    proyecto digital con identidad propia.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-plum text-ivory" data-testid="section-valores">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-dustyrose font-sans font-semibold">
              Nuestros valores
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-4 leading-[1.15]">
              Lo que sostiene cada proyecto.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/10 border border-ivory/10 mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div data-testid={`value-card-${v.title.toLowerCase()}`} className="bg-plum p-8 lg:p-10 h-full">
                  <h3 className="font-serif text-2xl text-ivory">{v.title}</h3>
                  <p className="mt-4 text-sm font-light text-[#C9B8BE] leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink leading-[1.15]">
              Conversemos sobre tu proyecto.
            </h2>
            <p className="mt-6 text-base font-light text-stone2 leading-relaxed max-w-xl mx-auto">
              La primera reunión es informativa y no te obliga a contratar.
            </p>
            <Link
              to="/postula"
              data-testid="nosotros-cta-postula"
              className="mt-10 inline-flex items-center gap-2 bg-plum text-ivory hover:bg-mauve px-10 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 font-sans"
            >
              Solicitar una reunión <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
